import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { LoadingIcon } from '@/assets/icons/loading-icon'
import { FloatLabel } from '@/components/float-label'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface ComboboxItems {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  data: any
  value: string
  label: string
}

interface ComboboxProps {
  items: ComboboxItems[]
  label: string
  notFoundMessage: string
  placeholder: string
  className?: string
  loadingOptions?: boolean
  selectedValue?: string
  onChange?: (value: ComboboxItems) => void
}

export const Combobox = ({
  items,
  label,
  placeholder,
  notFoundMessage,
  className,
  loadingOptions,
  onChange,
  selectedValue
}: ComboboxProps) => {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')

  useEffect(() => {
    if (selectedValue) {
      const found = items.find((item) => {
        return item.value === selectedValue
      })
      if (found) {
        setValue(found.value)
      }
    }
  }, [items, selectedValue])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <FloatLabel
        label={label}
        variant={'floating'}
        open={open}
        hasValue={!!value && `${value}`.trim() !== ''}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn('w-[200px] h-10 justify-between', className)}
          >
            {value ? items.find((item) => item.value === value)?.label : ''}
          </Button>
        </PopoverTrigger>
      </FloatLabel>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command
          filter={(_value, search, keywords) => {
            if (keywords) {
              const label = keywords[0]
              if (label.toLowerCase().includes(search.toLowerCase())) {
                return 1
              }
              return 0
            }

            return 0
          }}
        >
          <CommandInput placeholder={placeholder} className="h-9" />
          {loadingOptions && (
            <div className="w-full my-5 flex justify-center text-[var(--brand-blue-1)]">
              <LoadingIcon />
            </div>
          )}
          {!loadingOptions && (
            <CommandList>
              <CommandEmpty>{notFoundMessage}</CommandEmpty>
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.value}
                    keywords={[item.label]}
                    onSelect={(currentValue) => {
                      setValue(currentValue === value ? '' : currentValue)
                      onChange?.(item)
                      setOpen(false)
                    }}
                  >
                    {item.label}
                    <Check
                      className={cn(
                        'ml-auto',
                        value === item.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
